import { useState, useCallback, useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { GalleryGrid } from './GalleryGrid';
import { GalleryTimeline } from './GalleryTimeline';
import { ImageViewer } from './ImageViewer';
import { ImageDetailsPanel } from './ImageDetailsPanel';
import { AlbumManagerDialog } from './AlbumManagerDialog';
import { GalleryUploadQueue } from './GalleryUploadQueue';
import { useGalleryUploadQueue } from './useGalleryUploadQueue';
import type { GalleryUploadAnnouncement } from './useGalleryUploadQueue';
import { useGalleryPageDrop } from './useGalleryPageDrop';
import { useGalleryImages } from '../../hooks/useGalleryImages';
import {
  useGalleryAlbums,
  useGalleryTags,
  useInvalidateAfterImageDelete,
} from '../../hooks/useGalleryOrganisation';
import { useMutation } from '@tanstack/react-query';
import { deleteGalleryImage, deleteGalleryImages } from '../../services/galleryService';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { Alert } from '../atoms/Alert/Alert';
import { PhotoUploadDropzone } from '../atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { PHOTO_MAX_SIZE_BYTES, splitPhotoFiles } from '../atoms/PhotoUploadDropzone/photoFileValidation';
import { preparePhotoFiles } from '../atoms/PhotoUploadDropzone/photoDownscale';
import type { GalleryImage, GalleryViewMode, TimelineGroupBy } from '../../types/gallery';
import { Button } from '../atoms/Button/Button';
import { Select } from '../atoms/Select/Select';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { useTranslation } from '../../i18n/useTranslation';
import './GalleryContainer.css';

export interface GalleryContainerProps {
  aquariumId: string;
  aquariumName: string;
}

export function GalleryContainer({ aquariumId, aquariumName }: GalleryContainerProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<GalleryViewMode>('grid');
  const [groupBy, setGroupBy] = useState<TimelineGroupBy>('month');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [albumFilter, setAlbumFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [albumManagerOpen, setAlbumManagerOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  /** What the delete confirmation is about: one image (from the viewer) or the selection. */
  const [deleteTarget, setDeleteTarget] = useState<{ kind: 'single'; image: GalleryImage } | { kind: 'selection' } | null>(
    null,
  );
  const [deleteAnnouncement, setDeleteAnnouncement] = useState('');
  const deleteErrorRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const selectStartRef = useRef<HTMLButtonElement>(null);
  const invalidateAfterDelete = useInvalidateAfterImageDelete(aquariumId);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const upload = useGalleryUploadQueue(aquariumId);
  const { addFiles } = upload;
  const albums = useGalleryAlbums(aquariumId);
  const tags = useGalleryTags(aquariumId);

  const { images, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error, refetch } =
    useGalleryImages({ aquariumId, albumId: albumFilter || null, tagId: tagFilter || null });
  const filtered = albumFilter !== '' || tagFilter !== '';

  // An edit in the viewer (e.g. removing the tag that is filtered on) can shrink the list.
  useEffect(() => {
    if (!viewerOpen) return;
    if (images.length === 0) setViewerOpen(false);
    else if (viewerIndex > images.length - 1) setViewerIndex(images.length - 1);
  }, [images.length, viewerIndex, viewerOpen]);

  // Stepping through the photos in the viewer stays within the current filter and
  // loads the next page before the end, so prev/next/slideshow reach every photo.
  useEffect(() => {
    if (viewerOpen && hasNextPage && !isFetchingNextPage && viewerIndex >= images.length - 2) {
      void fetchNextPage();
    }
  }, [viewerOpen, viewerIndex, images.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first && first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleImageClick = useCallback((index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  }, []);

  const handleViewerClose = useCallback(() => {
    setViewerOpen(false);
  }, []);

  const handleViewerPrev = useCallback(() => {
    setViewerIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleViewerNext = useCallback(() => {
    setViewerIndex((prev) => Math.min(images.length - 1, prev + 1));
  }, [images.length]);

  const handleViewerSelect = useCallback((index: number) => {
    setViewerIndex(index);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleViewModeGrid = useCallback(() => {
    setViewMode('grid');
  }, []);

  const handleViewModeTimeline = useCallback(() => {
    setViewMode('timeline');
  }, []);

  const handleGroupByChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setGroupBy(e.target.value as TimelineGroupBy);
    },
    [],
  );

  const handleAlbumFilter = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setAlbumFilter(e.target.value);
  }, []);

  const handleTagFilter = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setTagFilter(e.target.value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setAlbumFilter('');
    setTagFilter('');
  }, []);

  const handleAlbumDeleted = useCallback((albumId: string) => {
    setAlbumFilter((current) => (current === albumId ? '' : current));
  }, []);

  const handleOpenAlbumManager = useCallback(() => setAlbumManagerOpen(true), []);
  const handleCloseAlbumManager = useCallback(() => setAlbumManagerOpen(false), []);

  const handleStartSelection = useCallback(() => {
    setViewMode('grid');
    setSelectionMode(true);
    setSelectedIds(new Set());
  }, []);

  const handleCancelSelection = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  const handleToggleSelect = useCallback((imageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  }, []);

  const deleteMutation = useMutation({
    mutationFn: async (target: NonNullable<typeof deleteTarget>): Promise<number> => {
      if (target.kind === 'single') {
        await deleteGalleryImage(aquariumId, target.image.id);
        return 1;
      }
      const deleted = await deleteGalleryImages(aquariumId, Array.from(selectedIds));
      return deleted.length;
    },
    onSuccess: (count, target) => {
      setDeleteTarget(null);
      setDeleteAnnouncement(t('gallery.delete.done', { n: count }));
      if (target.kind === 'selection') {
        setSelectionMode(false);
        setSelectedIds(new Set());
        // The selection bar is gone: focus "Select" again (or Upload when nothing is left).
        window.setTimeout(() => (selectStartRef.current ?? uploadButtonRef.current)?.focus(), 0);
      } else if (images.length <= 1) {
        // The last photo is gone: close the viewer and keep focus on the page.
        setViewerOpen(false);
        window.setTimeout(() => uploadButtonRef.current?.focus(), 0);
      }
      // Otherwise the viewer stays open; the refreshed list moves it to the next photo.
      invalidateAfterDelete();
    },
    onError: () => {
      setDeleteTarget(null);
      window.setTimeout(() => deleteErrorRef.current?.focus(), 0);
    },
  });

  const handleDeleteRequest = useCallback((image: GalleryImage) => {
    deleteMutation.reset();
    setDeleteTarget({ kind: 'single', image });
  }, [deleteMutation]);

  const handleDeleteSelection = useCallback(() => {
    deleteMutation.reset();
    setDeleteTarget({ kind: 'selection' });
  }, [deleteMutation]);

  const renderDetails = useCallback(
    (image: GalleryImage) => <ImageDetailsPanel key={image.id} aquariumId={aquariumId} image={image} />,
    [aquariumId],
  );

  // Both upload buttons open the dropzone's file dialog, so there is one picker.
  const handleUploadClick = useCallback(() => {
    dropzoneRef.current?.click();
  }, []);

  const handlePageDrop = useCallback(
    (files: File[]) => {
      // As in the dropzone: iPhone photos (HEIC) → JPEG, photos over 10 MB downscaled.
      void preparePhotoFiles(files, PHOTO_MAX_SIZE_BYTES).then((converted) => {
        const { accepted, rejected } = splitPhotoFiles(converted);
        addFiles(accepted, rejected);
      });
    },
    [addFiles],
  );
  // Dropping anywhere on the page is disabled while a modal (viewer, album manager) is open.
  const isPageDragOver = useGalleryPageDrop(
    !viewerOpen && !albumManagerOpen && deleteTarget === null,
    handlePageDrop,
  );

  return (
    <div className="gallery-container" data-testid="gallery-container">
      <div className="gallery-container__toolbar" data-testid="gallery-toolbar">
        <div className="gallery-container__view-toggle" role="group" aria-label={t('gallery.viewMode')}>
          <Button
            variant="ghost"
            className={
              viewMode === 'grid'
                ? 'gallery-container__toggle-btn gallery-container__toggle-btn--active'
                : 'gallery-container__toggle-btn'
            }
            onClick={handleViewModeGrid}
            aria-pressed={viewMode === 'grid'}
            data-testid="view-mode-grid"
          >
            {t('gallery.viewGrid')}
          </Button>
          <Button
            variant="ghost"
            className={
              viewMode === 'timeline'
                ? 'gallery-container__toggle-btn gallery-container__toggle-btn--active'
                : 'gallery-container__toggle-btn'
            }
            onClick={handleViewModeTimeline}
            aria-pressed={viewMode === 'timeline'}
            data-testid="view-mode-timeline"
          >
            {t('gallery.viewTimeline')}
          </Button>
        </div>

        {viewMode === 'timeline' && (
          <div className="gallery-container__groupby" data-testid="groupby-selector">
            <Select
              id="gallery-groupby"
              label={t('gallery.groupBy')}
              className="gallery-container__groupby-select"
              value={groupBy}
              onChange={handleGroupByChange}
              data-testid="groupby-select"
            >
              <option value="day">{t('gallery.groupDay')}</option>
              <option value="week">{t('gallery.groupWeek')}</option>
              <option value="month">{t('gallery.groupMonth')}</option>
              <option value="year">{t('gallery.groupYear')}</option>
            </Select>
          </div>
        )}

        {/* No count while loading: "Bilder: 0" read as "my photos are gone" (usability audit §5.5). */}
        {!(isLoading && images.length === 0) && (
          <span className="gallery-container__count" data-testid="gallery-image-count">
            {t('gallery.imageCount', { n: images.length })}
          </span>
        )}

        <Button
          ref={uploadButtonRef}
          variant="primary"
          className="gallery-container__upload-btn"
          onClick={handleUploadClick}
          data-testid="gallery-upload-btn"
        >
          {t('gallery.uploadButton')}
        </Button>
      </div>

      <div
        className="gallery-container__filters"
        role="group"
        aria-label={t('gallery.filters.label')}
        data-testid="gallery-filters"
      >
        <Select
          id="gallery-album-filter"
          label={t('gallery.filters.album')}
          value={albumFilter}
          onChange={handleAlbumFilter}
          data-testid="gallery-album-filter"
        >
          <option value="">{t('gallery.filters.allAlbums')}</option>
          {albums.items.map((album) => (
            <option key={album.id} value={album.id}>
              {album.name}
            </option>
          ))}
        </Select>
        <Select
          id="gallery-tag-filter"
          label={t('gallery.filters.tag')}
          value={tagFilter}
          onChange={handleTagFilter}
          data-testid="gallery-tag-filter"
        >
          <option value="">{t('gallery.filters.allTags')}</option>
          {tags.items.map((tag) => (
            <option key={tag.id} value={tag.id}>
              {tag.name}
            </option>
          ))}
        </Select>
        <Button variant="secondary" onClick={handleOpenAlbumManager} data-testid="gallery-manage-albums">
          {t('gallery.albumManager.manage')}
        </Button>
        {filtered && (
          <Button variant="ghost" onClick={handleResetFilters} data-testid="gallery-reset-filters">
            {t('gallery.filters.reset')}
          </Button>
        )}
        {images.length > 0 && !selectionMode && (
          <Button ref={selectStartRef} variant="secondary" onClick={handleStartSelection} data-testid="gallery-select-start">
            {t('gallery.select.start')}
          </Button>
        )}
      </div>

      {selectionMode && (
        <div
          className="gallery-container__selection-bar"
          role="group"
          aria-label={t('gallery.select.barLabel')}
          data-testid="gallery-selection-bar"
        >
          <Text as="span" variant="body-sm">{t('gallery.select.count', { n: selectedIds.size })}</Text>
          <Button
            variant="danger"
            onClick={handleDeleteSelection}
            disabled={selectedIds.size === 0}
            data-testid="gallery-delete-selected"
          >
            {t('gallery.select.deleteSelected', { n: selectedIds.size })}
          </Button>
          <Button variant="ghost" onClick={handleCancelSelection}>
            {t('gallery.select.cancel')}
          </Button>
        </div>
      )}

      {deleteMutation.isError && (
        <Alert ref={deleteErrorRef} tabIndex={-1} onDismiss={() => deleteMutation.reset()} dismissLabel={t('common.close')}>
          {t('gallery.delete.error')}
        </Alert>
      )}
      <LiveRegion visuallyHidden data-testid="gallery-delete-announcer">{deleteAnnouncement}</LiveRegion>

      <PhotoUploadDropzone
        ref={dropzoneRef}
        multiple
        onFilesChange={addFiles}
      />

      <GalleryUploadQueue
        items={upload.items}
        busy={upload.busy}
        onRetry={upload.retry}
        onClear={upload.clearFinished}
      />

      <LiveRegion visuallyHidden aria-atomic="true" data-testid="gallery-upload-announcer">
        {formatAnnouncement(upload.announcement, t)}
      </LiveRegion>

      {error && images.length === 0 ? (
        <LoadErrorState
          title={t('gallery.loadError')}
          onRetry={refetch}
          retryLabel={t('common.retry')}
          data-testid="gallery-load-error"
        />
      ) : isLoading && images.length === 0 ? (
        <div className="gallery-container__loading" data-testid="gallery-loading">
          <GalleryGrid
            images={[]}
            onImageClick={handleImageClick}
            isLoading
            hasNextPage={false}
            onLoadMore={handleLoadMore}
          />
        </div>
      ) : images.length === 0 && filtered ? (
        <div className="gallery-container__empty" data-testid="gallery-empty-filtered">
          <svg
            className="gallery-container__empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M3 5h18l-7 8v6l-4-2v-4z" />
          </svg>
          <Heading level={2} className="gallery-container__empty-title">
            {t('gallery.filters.emptyHeadline')}
          </Heading>
          <Text className="gallery-container__empty-message">{t('gallery.filters.emptyDescription')}</Text>
          <Button variant="primary" onClick={handleResetFilters}>
            {t('gallery.filters.reset')}
          </Button>
        </div>
      ) : images.length === 0 ? (
        <div className="gallery-container__empty" data-testid="gallery-empty">
          <svg
            className="gallery-container__empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <Heading level={2} className="gallery-container__empty-title">
            {t('gallery.emptyHeadline')}
          </Heading>
          <Text className="gallery-container__empty-message">
            {t('gallery.emptyDescription')}
          </Text>
          <Button variant="primary" onClick={handleUploadClick}>
            {t('gallery.uploadButton')}
          </Button>
        </div>
      ) : viewMode === 'grid' ? (
        <GalleryGrid
          images={images}
          onImageClick={handleImageClick}
          isLoading={isFetchingNextPage}
          hasNextPage={hasNextPage ?? false}
          onLoadMore={handleLoadMore}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
        />
      ) : (
        <GalleryTimeline
          images={images}
          groupBy={groupBy}
          onImageClick={handleImageClick}
        />
      )}

      <div
        ref={loadMoreRef}
        className="gallery-container__load-more-sentinel"
        aria-hidden="true"
        data-testid="load-more-sentinel"
      />

      {isFetchingNextPage && (
        <div className="gallery-container__fetch-more" data-testid="gallery-fetch-more">
          {t('common.loading')}
        </div>
      )}

      {isPageDragOver && (
        // Pointer-only affordance; the dropzone above is the keyboard/screen-reader path.
        <div className="gallery-container__drop-overlay" aria-hidden="true" data-testid="gallery-drop-overlay">
          <span className="gallery-container__drop-overlay-label">{t('gallery.uploadQueue.dropOverlay')}</span>
        </div>
      )}

      <ImageViewer
        images={images}
        viewerState={{ isOpen: viewerOpen, currentIndex: viewerIndex, scale: 1, pan: { x: 0, y: 0 } }}
        aquariumName={aquariumName}
        onClose={handleViewerClose}
        onPrev={handleViewerPrev}
        onNext={handleViewerNext}
        onSelect={handleViewerSelect}
        renderDetails={renderDetails}
        onDeleteRequest={handleDeleteRequest}
        suspended={deleteTarget !== null}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={
          deleteTarget?.kind === 'selection'
            ? t('gallery.delete.confirmTitleMany', { n: selectedIds.size })
            : t('gallery.delete.confirmTitle')
        }
        message={t('gallery.delete.confirmMessage')}
        confirmLabel={t('gallery.delete.confirm')}
        cancelLabel={t('common.cancel')}
        isConfirming={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <AlbumManagerDialog
        aquariumId={aquariumId}
        isOpen={albumManagerOpen}
        onClose={handleCloseAlbumManager}
        onDeleted={handleAlbumDeleted}
      />
    </div>
  );
}

function formatAnnouncement(
  announcement: GalleryUploadAnnouncement | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (announcement === null) return '';
  if (announcement.kind === 'queued') {
    const parts: string[] = [];
    if (announcement.queued > 0) parts.push(t('gallery.uploadQueue.announceQueued', { n: announcement.queued }));
    if (announcement.rejected > 0) parts.push(t('gallery.uploadQueue.announceRejected', { n: announcement.rejected }));
    return parts.join(' ');
  }
  return t('gallery.uploadQueue.announceFinished', { done: announcement.done, failed: announcement.failed });
}
